<template>
  <div class="dashboard-analytics-container">

    <SwitchComponent v-if="showSwitchModal" :targetRoute="switchTargetRoute" :requiredLevel="switchRequiredLevel" @closeModalSwitch="showSwitchModal = false" />

    <!-- Error State -->
    <div v-if="analyticsError" class="alert alert-warning d-flex align-items-center justify-content-between mb-4" role="alert">
      <div class="d-flex align-items-center gap-2">
        <i class="fas fa-exclamation-triangle"></i>
        <div>
          <strong>Failed to load analytics data</strong>
          <p class="mb-0 small">{{ analyticsError }}</p>
        </div>
      </div>
      <button class="btn btn-sm btn-outline-warning" @click="retryFetch">
        <i class="fas fa-sync-alt me-1"></i> Retry
      </button>
    </div>

    <!-- Skeleton Loading -->
    <DashboardSkeleton v-if="analyticsLoading && !analyticsKpi" />

    <!-- Row 1: KPI Cards -->
    <div v-show="!analyticsLoading || analyticsKpi" class="row g-4 mb-4">

      <div class="col-xl-3 col-md-6">
        <KpiCard
          label="NEW MEMBERS"
          default-icon="fa-user-plus"
          :value="kpiTotalMembers.value"
          :trend="kpiTotalMembers.trend"
          :previous-value="kpiTotalMembers.previousValue"
          :timeframe-text="timeframeText"
          total-label="Total"
          :total-value="kpiTotalMembers.totalValue"
          total-title="Total all-time members"
          click-title="Go to Member List"
          @click="performQuickAction('member')"
        />
      </div>

      <div class="col-xl-3 col-md-6">
        <KpiCard
          label="TRANSACTIONS"
          default-icon="fa-wallet"
          :value="kpiTransactions.value"
          :trend="kpiTransactions.trend"
          :previous-value="kpiTransactions.previousValue"
          :timeframe-text="timeframeText"
          click-title="Go to Transaction Report"
          @click="performQuickAction('transaction')"
        />
      </div>

      <div class="col-xl-3 col-md-6">
        <KpiCard
          label="REDEEMED & CLAIMED"
          default-icon="fa-ticket-alt"
          :value="kpiVouchers.value"
          :trend="kpiVouchers.trend"
          :previous-value="kpiVouchers.previousValue"
          :timeframe-text="timeframeText"
          total-label="Claimed"
          :total-value="kpiVouchers.totalValue"
          total-title="Vouchers claimed in this period"
          click-title="Go to Voucher Redeems"
          @click="performQuickAction('points-redeem')"
        />
      </div>

      <!-- Points Earned / Redeemed Cycling Card -->
      <div class="col-xl-3 col-md-6">
        <div
          class="analytic-kpi-card cycling-card overflow-hidden quick-action-card"
          @mouseenter="pausePointsSlide"
          @mouseleave="resumePointsSlide"
          @click="activePointsSlide === 'earned' ? performQuickAction('points-earned') : performQuickAction('points-redeem')"
          :title="activePointsSlide === 'earned' ? 'Go to Transactions' : 'Go to Voucher Redeems'"
        >
          <transition name="points-slide" mode="out-in">
            <div :key="activePointsSlide" class="slide-content">
              <div v-if="activePointsSlide === 'earned'">
                <div class="kpi-card-header d-flex justify-content-between align-items-center mb-3">
                  <span class="kpi-label text-indigo-dark">POINTS EARNED</span>
                  <div class="icon-circle primary-icon-circle kpi-icon-wrapper">
                    <i class="fas fa-coins default-icon"></i>
                    <i class="fas fa-external-link-alt hover-icon"></i>
                  </div>
                </div>
                <div class="kpi-body">
                  <h2 class="kpi-value mb-2">{{ kpiPointsEarned.value }}</h2>
                  <div class="kpi-footer d-flex align-items-center gap-1">
                    <span class="trend-indicator" :class="kpiPointsEarned.trend > 0 ? 'trend-up' : 'trend-down'">
                      <i class="fas" :class="kpiPointsEarned.trend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                      {{ Math.abs(kpiPointsEarned.trend) }}%
                    </span>
                    <span class="kpi-compare-text" :title="timeframeText.charAt(0).toUpperCase() + timeframeText.slice(1) + ': ' + kpiPointsEarned.previousValue">vs {{ timeframeText }}</span>
                  </div>
                </div>
              </div>
              <div v-else>
                <div class="kpi-card-header d-flex justify-content-between align-items-center mb-3">
                  <span class="kpi-label text-pink-dark">POINTS REDEEMED</span>
                  <div class="icon-circle primary-icon-circle kpi-icon-wrapper">
                    <i class="fas fa-hand-holding-heart default-icon"></i>
                    <i class="fas fa-external-link-alt hover-icon"></i>
                  </div>
                </div>
                <div class="kpi-body">
                  <h2 class="kpi-value mb-2">{{ kpiPointsSpent.value }}</h2>
                  <div class="kpi-footer d-flex align-items-center gap-1">
                    <span class="trend-indicator" :class="kpiPointsSpent.trend > 0 ? 'trend-up' : 'trend-down'">
                      <i class="fas" :class="kpiPointsSpent.trend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                      {{ Math.abs(kpiPointsSpent.trend) }}%
                    </span>
                    <span class="kpi-compare-text" :title="timeframeText.charAt(0).toUpperCase() + timeframeText.slice(1) + ': ' + kpiPointsSpent.previousValue">vs {{ timeframeText }}</span>
                  </div>
                </div>
              </div>
            </div>
          </transition>
          <div class="cycling-dots align-items-center">
            <i class="fas fa-chevron-left text-slate-muted" style="font-size: 0.65rem; cursor: pointer; padding: 2px;" @click.stop="setPointsSlide(activePointsSlide === 'earned' ? 'spent' : 'earned')"></i>
            <div class="cycle-dot" :class="{ active: activePointsSlide === 'earned' }" style="cursor: pointer;" @click.stop="setPointsSlide('earned')"></div>
            <div class="cycle-dot" :class="{ active: activePointsSlide === 'spent' }" style="cursor: pointer;" @click.stop="setPointsSlide('spent')"></div>
            <i class="fas fa-chevron-right text-slate-muted" style="font-size: 0.65rem; cursor: pointer; padding: 2px;" @click.stop="setPointsSlide(activePointsSlide === 'earned' ? 'spent' : 'earned')"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 2: Member Growth & Channel Distribution -->
    <div class="row g-4 mb-4">

      <!-- Member Registration Trend -->
      <div class="col-lg-8">
        <div class="analytic-chart-card h-100 d-flex flex-column justify-content-between">
          <div>
            <div class="card-chart-header mb-3 d-flex justify-content-between align-items-start">
              <div>
                <h5 class="fw-bold text-dark-slate mb-1">Member Registration Trend</h5>
                <p class="text-slate-muted small mb-0">Daily monitoring of net new sign-ups and acquisition velocity.</p>
              </div>
              <button class="btn btn-sm btn-light border action-btn" @click="performQuickAction('member-tracking')" title="Go to Member Tracking">
                <i class="fas fa-external-link-alt text-slate-muted"></i>
              </button>
            </div>
            <div class="chart-content">
              <apexchart type="area" height="260" :options="memberGrowthOptions" :series="memberGrowthSeries"></apexchart>
            </div>
          </div>

          <!-- Recent Registrations Horizontal Ticker -->
          <div class="recent-registrations-ticker mt-3">
            <div class="ticker-header d-flex justify-content-between align-items-center mb-2 px-1">
              <span class="ticker-title fw-bold text-dark-slate" style="font-size: 0.8rem;">
                <i class="fas fa-user-plus me-1" :style="{ color: categoryColors['Branch'] }"></i> Recent Registrations
              </span>
              <span class="badge bg-light text-slate-muted border px-2 py-1" style="font-size: 0.65rem; font-weight: 600;">Last 30 Days</span>
            </div>
            <div class="ticker-viewport">
              <div class="ticker-track">
                <div class="ticker-group">
                  <div class="ticker-item-card" v-for="(member, idx) in recentMembers" :key="'g1-' + idx">
                    <div class="d-flex align-items-center gap-2">
                      <div class="member-avatar-wrapper">
                        <img v-if="member.avatar" :src="member.avatar" class="member-avatar" />
                        <div v-else class="member-avatar-fallback" :style="{ backgroundColor: categoryColors[member.sourceType] + '15', color: categoryColors[member.sourceType] }">{{ member.initials }}</div>
                      </div>
                      <div class="member-info">
                        <div class="member-name fw-bold text-dark-slate">{{ member.name }}</div>
                        <div class="member-meta">
                          <span class="reg-date">{{ member.regDate }}</span>
                          <span class="meta-separator">•</span>
                          <span class="reg-source" :style="{ color: categoryColors[member.sourceType] }">{{ member.source }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <!-- Duplicate group for seamless loop -->
                <div class="ticker-group">
                  <div class="ticker-item-card" v-for="(member, idx) in recentMembers" :key="'g2-' + idx">
                    <div class="d-flex align-items-center gap-2">
                      <div class="member-avatar-wrapper">
                        <img v-if="member.avatar" :src="member.avatar" class="member-avatar" />
                        <div v-else class="member-avatar-fallback" :style="{ backgroundColor: categoryColors[member.sourceType] + '15', color: categoryColors[member.sourceType] }">{{ member.initials }}</div>
                      </div>
                      <div class="member-info">
                        <div class="member-name fw-bold text-dark-slate">{{ member.name }}</div>
                        <div class="member-meta">
                          <span class="reg-date">{{ member.regDate }}</span>
                          <span class="meta-separator">•</span>
                          <span class="reg-source" :style="{ color: categoryColors[member.sourceType] }">{{ member.source }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Channel Distribution -->
      <div class="col-lg-4">
        <div class="analytic-chart-card h-100 d-flex flex-column justify-content-between">
          <div class="mb-3 d-flex justify-content-between align-items-start">
            <div>
              <h5 class="fw-bold text-dark-slate mb-1">Channel Distribution</h5>
              <p class="text-slate-muted small mb-0">Proportion of members acquired grouped by channel source.</p>
            </div>
            <button class="btn btn-sm btn-light border action-btn" @click="performQuickAction('member-tracking')" title="Go to Member Tracking">
              <i class="fas fa-external-link-alt text-slate-muted"></i>
            </button>
          </div>
          <template v-if="registrationSources.length > 0">
            <div class="py-2 d-flex justify-content-center">
              <apexchart type="donut" height="180" :options="channelRankingOptions" :series="channelRankingSeries"></apexchart>
            </div>
            <div class="channel-list-wrapper position-relative pb-2">
              <div class="channel-list-detailed mt-2" ref="channelListRef" @scroll="checkScroll">
                <div class="channel-detail-row py-2 d-flex align-items-center justify-content-between" v-for="(source, idx) in registrationSources" :key="idx">
                  <div class="d-flex align-items-center gap-2">
                    <span class="color-dot" :style="{ backgroundColor: categoryColors[source.type] }"></span>
                    <div>
                      <div class="small fw-semibold text-dark-slate">{{ source.name }}</div>
                      <div class="text-slate-muted" style="font-size: 0.7rem;">{{ source.type }}</div>
                    </div>
                  </div>
                  <div class="text-end">
                    <span class="small fw-bold text-dark-slate">{{ source.count }}</span>
                    <div class="text-slate-muted" style="font-size: 0.7rem;">{{ getSourcePercentage(source.count) }}%</div>
                  </div>
                </div>
              </div>
              <div v-if="showScrollIndicator" class="scroll-more-btn" @click="scrollList">
                <i class="fas" :class="isScrolledToBottom ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
              </div>
            </div>
          </template>
          <div v-else class="empty-state d-flex flex-column align-items-center justify-content-center h-100 py-4">
            <div class="empty-icon-wrapper mb-3" style="width: 48px; height: 48px; border-radius: 50%; background: #F3F4F6; display: flex; align-items: center; justify-content: center;">
              <i class="fas fa-chart-pie text-slate-muted" style="font-size: 1.25rem;"></i>
            </div>
            <h6 class="fw-bold text-dark-slate mb-1">No Data Available</h6>
            <p class="text-slate-muted small text-center px-4">There is no registration data available for this period.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 3: Point Activity & Tier Movement -->
    <div class="row g-4 mb-4">

      <!-- Points Velocity -->
      <div class="col-lg-8">
        <div class="analytic-chart-card h-100">
          <div class="card-chart-header mb-4">
            <h5 class="fw-bold text-dark-slate mb-1">Points Velocity Earning vs Spending</h5>
            <p class="text-slate-muted small mb-0">Daily tracking of active points economy (earned vs redeemed).</p>
          </div>
          <div class="chart-content">
            <apexchart type="area" height="300" :options="pointActivityOptions" :series="pointActivitySeries"></apexchart>
          </div>
        </div>
      </div>

      <!-- Tier Level Distribution -->
      <div class="col-lg-4">
        <div class="analytic-chart-card h-100">
          <div class="card-chart-header mb-4 d-flex justify-content-between align-items-start">
            <div>
              <h5 class="fw-bold text-dark-slate mb-1">Tier Level Distribution</h5>
              <p class="text-slate-muted small mb-0">Distribution of members across available tiers.</p>
            </div>
            <button v-if="isCorporateLevel" class="btn btn-sm btn-light border action-btn" @click="performQuickAction('membertier')" title="Go to Member Tiers">
              <i class="fas fa-external-link-alt text-slate-muted"></i>
            </button>
          </div>
          <div class="chart-content">
            <apexchart type="bar" height="300" :options="tierMovementOptions" :series="tierMovementSeries"></apexchart>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 4: Tables Section -->
    <div class="row g-4">

      <!-- Top Performing Products -->
      <div class="col-xl-4 col-md-6">
        <div class="analytic-table-card h-100">
          <div class="mb-3 d-flex justify-content-between align-items-start">
            <div>
              <h5 class="fw-bold text-dark-slate mb-1">Top Performing Products</h5>
              <p class="text-slate-muted small mb-0">Ranked by total volume sold.</p>
            </div>
            <button class="btn btn-sm btn-light border action-btn" @click="performQuickAction('product')" title="Go to Products">
              <i class="fas fa-external-link-alt text-slate-muted"></i>
            </button>
          </div>
          <div class="channel-list-wrapper position-relative pb-2 mt-3">
            <div class="channel-list-detailed" style="max-height: 340px; overflow-y: auto;" ref="productsListRef" @scroll="checkProductsScroll">
              <table class="table table-borderless align-middle m-0 premium-table">
                <thead style="position: sticky; top: 0; z-index: 1; background: white;">
                  <tr>
                    <th style="width: 10%">Rank</th>
                    <th style="width: 70%">Product Name</th>
                    <th style="width: 20%" class="text-end">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in topProducts" :key="idx">
                    <td><span class="rank-badge" :class="'rank-' + (idx + 1)">{{ idx + 1 }}</span></td>
                    <td>
                      <div class="product-info-cell">
                        <div class="fw-semibold text-dark-slate small">
                          <span class="position-relative d-inline-block tooltip-text-trigger" @mouseenter="item.showTip = true" @mouseleave="item.showTip = false">
                              {{ item.name }}
                              <Transition name="fade">
                                  <div v-if="item.showTip" class="custom-entity-tip">
                                      <div class="d-flex flex-column gap-1">
                                          <span class="text-nowrap d-flex align-items-center gap-2"><i class="fas fa-code-branch flex-shrink-0 text-muted tooltip-row-icon"></i> <span class="fw-medium text-white">{{ item.branch_name }}</span></span>
                                          <span class="text-nowrap d-flex align-items-center gap-2"><i class="fas fa-store flex-shrink-0 text-muted tooltip-row-icon"></i> <span class="fw-medium text-white">{{ item.merchant_name }}</span></span>
                                      </div>
                                  </div>
                              </Transition>
                          </span>
                        </div>
                        <div class="progress-bar-container mt-1">
                          <div class="progress-bar-fill" :style="{ width: getProductPercentage(item.qty) + '%', backgroundColor: categoryColors['Branch'] }"></div>
                        </div>
                      </div>
                    </td>
                    <td class="text-end fw-bold text-slate-muted small">{{ formatNumber(item.qty) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="showProductsScrollIndicator" class="scroll-more-btn" @click="scrollProductsList">
              <i class="fas" :class="isProductsScrolledToBottom ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Top Redeemed Vouchers -->
      <div class="col-xl-4 col-md-6">
        <div class="analytic-table-card h-100">
          <div class="mb-3 d-flex justify-content-between align-items-start">
            <div>
              <h5 class="fw-bold text-dark-slate mb-1">Top Redeemed Vouchers</h5>
              <p class="text-slate-muted small mb-0">Most popular voucher redemptions.</p>
            </div>
            <button class="btn btn-sm btn-light border action-btn" @click="performQuickAction('points-redeem')" title="Go to Voucher Redeems">
              <i class="fas fa-external-link-alt text-slate-muted"></i>
            </button>
          </div>
          <div class="channel-list-wrapper position-relative pb-2 mt-3">
            <div class="channel-list-detailed" style="max-height: 340px; overflow-y: auto;" ref="vouchersListRef" @scroll="checkVouchersScroll">
              <table class="table table-borderless align-middle m-0 premium-table">
                <thead style="position: sticky; top: 0; z-index: 1; background: white;">
                  <tr>
                    <th style="width: 10%">Rank</th>
                    <th style="width: 65%">Voucher Title</th>
                    <th style="width: 25%" class="text-end">Redeemed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in topVouchers" :key="idx">
                    <td><span class="rank-badge" :class="'rank-' + (idx + 1)">{{ idx + 1 }}</span></td>
                    <td>
                      <div class="voucher-info-cell">
                        <div class="fw-semibold text-dark-slate small">
                          <span class="position-relative d-inline-block tooltip-text-trigger" @mouseenter="item.showTip = true" @mouseleave="item.showTip = false">
                              {{ item.name }}
                              <Transition name="fade">
                                  <div v-if="item.showTip" class="custom-entity-tip">
                                      <div class="d-flex flex-column gap-1">
                                          <span class="text-nowrap d-flex align-items-center gap-2"><i class="fas fa-code-branch flex-shrink-0 text-muted tooltip-row-icon"></i> <span class="fw-medium text-white">{{ item.branch_name }}</span></span>
                                          <span class="text-nowrap d-flex align-items-center gap-2"><i class="fas fa-store flex-shrink-0 text-muted tooltip-row-icon"></i> <span class="fw-medium text-white">{{ item.merchant_name }}</span></span>
                                      </div>
                                  </div>
                              </Transition>
                          </span>
                        </div>
                        <div class="progress-bar-container mt-1">
                          <div class="progress-bar-fill" :style="{ width: getVoucherPercentage(item.claim_count) + '%', backgroundColor: categoryColors['Branch'] }"></div>
                        </div>
                      </div>
                    </td>
                    <td class="text-end fw-bold text-dark-slate small">{{ formatNumber(item.claim_count) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="showVouchersScrollIndicator" class="scroll-more-btn" @click="scrollVouchersList">
              <i class="fas" :class="isVouchersScrolledToBottom ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Members at Risk -->
      <div class="col-xl-4 col-md-12">
        <div class="analytic-table-card h-100">
          <div class="mb-3 d-flex justify-content-between align-items-start">
            <div>
              <h5 class="fw-bold text-dark-slate mb-1" style="color: #1e293b !important;">Members at Risk</h5>
              <p class="text-slate-muted small mb-0">Warning trigger threshold is set to &lt; {{ riskThresholdPercentage }}% from tier limit.</p>
            </div>
            <button v-if="isCorporateLevel" class="btn btn-sm btn-light border action-btn" @click="performQuickAction('membertier-threshold')" title="View Threshold Settings">
              <i class="fas fa-cog text-slate-muted"></i>
            </button>
          </div>
          <div class="channel-list-wrapper position-relative pb-2 mt-3">
            <div class="channel-list-detailed" style="max-height: 340px; overflow-y: auto;" ref="riskListRef" @scroll="checkRiskScroll">
              <table class="table table-borderless align-middle m-0 premium-table">
                <thead style="position: sticky; top: 0; z-index: 1; background: white;">
                  <tr>
                    <th style="width: 60%">Member Details</th>
                    <th style="width: 40%" class="text-end">Requirement Gap</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, idx) in membersAtRisk" :key="idx">
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <div class="avatar-circle">{{ item.initials }}</div>
                        <div>
                          <div class="fw-semibold text-dark-slate small">{{ item.name }}</div>
                          <div class="d-flex align-items-center gap-1">
                            <span class="tier-pill">{{ item.tier }}</span>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td class="text-end">
                      <span class="gap-value small fw-bold" :style="{ color: categoryColors['Branch'] }">{{ item.gap_text }}</span>
                      <div class="text-slate-muted small-text mt-1">Downgrades on {{ item.expiry_date }}</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="showRiskScrollIndicator" class="scroll-more-btn" @click="scrollRiskList">
              <i class="fas" :class="isRiskScrolledToBottom ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, onBeforeUnmount } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import store from '@/store/'
import { useRouter } from 'vue-router'
import KpiCard from '@/components/dashboard/KpiCard.vue'
import DashboardSkeleton from '@/components/dashboard/DashboardSkeleton.vue'
import SwitchComponent from '@/views/Switch.vue'

export default {
    props: ['gain', 'dashboardMode', 'externalTimeframe'],
    components: {
        apexchart: VueApexCharts,
        KpiCard,
        DashboardSkeleton,
        SwitchComponent,
    },
    setup(props) {
        const router = useRouter();
        const timeframe = ref(props.externalTimeframe || 'this_month');
        const activePointsSlide = ref('earned');
        let pointsInterval = null;

        const isCorporateLevel = computed(() => {
            const selectedProfile = store.state.Auth?.selectedBusinessProfile || JSON.parse(localStorage.getItem('selected_business_profile') || '{}');
            return !selectedProfile.branch_id && !selectedProfile.merchant_id;
        });

        // ── Points slide cycling animation ─────────────────────────────────────
        const startPointsSlide = () => {
            if (pointsInterval) clearInterval(pointsInterval);
            pointsInterval = setInterval(() => {
                activePointsSlide.value = activePointsSlide.value === 'earned' ? 'spent' : 'earned';
            }, 4000);
        };
        const pausePointsSlide  = () => { if (pointsInterval) clearInterval(pointsInterval); };
        const resumePointsSlide = () => { startPointsSlide(); };
        const setPointsSlide = (slide) => {
            activePointsSlide.value = slide;
            startPointsSlide();
        };

        const showSwitchModal = ref(false);
        const switchTargetRoute = ref('Home');
        const switchRequiredLevel = ref('');

        // ── Quick action navigation ─────────────────────────────────────────────
        const performQuickAction = async (action) => {
            const autoSwitch = async (level, targetRoute) => {
                const selectedProfile = store.state.Auth?.selectedBusinessProfile || JSON.parse(localStorage.getItem('selected_business_profile') || '{}');
                
                let needsSwitch = false;
                if (level === 'merchant' && !selectedProfile.merchant_id) {
                    needsSwitch = true;
                } else if (level === 'branch' && !selectedProfile.branch_id) {
                    needsSwitch = true;
                }

                if (!needsSwitch) {
                    router.push(targetRoute);
                    return;
                }

                switchTargetRoute.value = targetRoute;
                switchRequiredLevel.value = level;
                showSwitchModal.value = true;
            };

            if      (action === 'member')               router.push({ path: '/member', query: { newMember: 'true' } });
            else if (action === 'transaction') {
                if (props.dashboardMode === 'merchant') {
                    router.push({ path: '/transaction', query: { timeframe: timeframe.value } });
                } else {
                    router.push({ path: '/transaction-report', query: { timeframe: timeframe.value } });
                }
            }
            else if (action === 'voucher')               autoSwitch('merchant', '/voucher');
            else if (action === 'points-earned')         autoSwitch('merchant', '/transaction');
            else if (action === 'points-redeem')         autoSwitch('merchant', '/voucher/redeem');
            else if (action === 'member-tracking')       router.push({ path: '/member/tracking', query: { timeframe: timeframe.value } });
            else if (action === 'product')               autoSwitch('branch', '/branch/product');
            else if (action === 'membertier')            router.push('/membertier');
            else if (action === 'membertier-threshold')  router.push('/membertier/threshold');
        };

        // ── Timeframe label for trend comparison ───────────────────────────────
        const timeframeText = computed(() => {
            if (timeframe.value === 'today')     return 'yesterday';
            if (timeframe.value === 'this_week') return 'last week';
            if (timeframe.value === 'this_year') return 'last year';
            return 'last month';
        });

        // ── Lifecycle ───────────────────────────────────────────────────────────
        onMounted(() => {
            startPointsSlide();
        });

        onBeforeUnmount(() => {
            if (pointsInterval) clearInterval(pointsInterval);
        });

        // ── Vuex store state ────────────────────────────────────────────────────
        const analyticsKpi          = computed(() => store.state.DashboardAnalytics?.kpi);
        const analyticsMemberGrowth = computed(() => store.state.DashboardAnalytics?.memberGrowth);
        const analyticsChannel      = computed(() => store.state.DashboardAnalytics?.channelRanking);
        const analyticsPoints       = computed(() => store.state.DashboardAnalytics?.pointActivity);
        const analyticsTier         = computed(() => store.state.DashboardAnalytics?.tierDistribution);
        const analyticsProducts     = computed(() => store.state.DashboardAnalytics?.topProducts);
        const analyticsVouchers     = computed(() => store.state.DashboardAnalytics?.topVouchers);
        const analyticsRisk         = computed(() => store.state.DashboardAnalytics?.membersAtRisk);
        const analyticsLoading      = computed(() => store.state.DashboardAnalytics?.status === 'loading');
        const analyticsError        = computed(() => store.state.DashboardAnalytics?.errMessage);

        const retryFetch = () => {
            store.dispatch('DashboardAnalytics/fetchAll', { timeframe: timeframe.value }).catch((err) => {
                console.error('[Dashboard] Retry failed:', err);
            });
        };

        // ── KPI computed values — safe fallbacks during loading ─────────────────
        const formatCurrency = (val) => {
            if (val == null || val === '-') return '-';
            return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(val);
        };
        const formatNumber = (val) => {
            if (val == null || val === '-') return '-';
            return new Intl.NumberFormat('id-ID').format(val);
        };

        const kpiTotalMembers = computed(() => ({
            value:         formatNumber(analyticsKpi.value?.total_members?.period_new),
            totalValue:    formatNumber(analyticsKpi.value?.total_members?.value),
            trend:         analyticsKpi.value?.total_members?.trend_percent ?? 0,
            previousValue: formatNumber(analyticsKpi.value?.total_members?.previous_new),
        }));
        const kpiTransactions = computed(() => ({
            value:         formatCurrency(analyticsKpi.value?.transactions?.value),
            trend:         analyticsKpi.value?.transactions?.trend_percent ?? 0,
            previousValue: formatCurrency(analyticsKpi.value?.transactions?.previous_value),
        }));
        const kpiVouchers = computed(() => ({
            value:         formatNumber(analyticsKpi.value?.redeemed_claimed_vouchers?.period_new),
            totalValue:    formatNumber(analyticsKpi.value?.redeemed_claimed_vouchers?.value),
            trend:         analyticsKpi.value?.redeemed_claimed_vouchers?.trend_percent ?? 0,
            previousValue: formatNumber(analyticsKpi.value?.redeemed_claimed_vouchers?.previous_new),
        }));
        const kpiPointsEarned = computed(() => ({
            value:         formatNumber(analyticsKpi.value?.points_earned?.value),
            trend:         analyticsKpi.value?.points_earned?.trend_percent ?? 0,
            previousValue: formatNumber(analyticsKpi.value?.points_earned?.previous_value),
        }));
        const kpiPointsSpent = computed(() => ({
            value:         formatNumber(analyticsKpi.value?.points_redeemed?.value),
            trend:         analyticsKpi.value?.points_redeemed?.trend_percent ?? 0,
            previousValue: formatNumber(analyticsKpi.value?.points_redeemed?.previous_value),
        }));

        // ── Chart series from store ─────────────────────────────────────────────
        const memberGrowthSeries   = computed(() => analyticsMemberGrowth.value?.chart_series ?? []);
        const channelRankingSeries = computed(() => analyticsChannel.value?.donut_series ?? []);
        const pointActivitySeries  = computed(() => analyticsPoints.value?.chart_series ?? []);
        const tierMovementSeries   = computed(() => analyticsTier.value?.chart_series ?? []);

        // ── List / table data ───────────────────────────────────────────────────
        const registrationSources     = computed(() => analyticsChannel.value?.source_list ?? []);
        const topProducts             = computed(() => analyticsProducts.value?.products ?? []);
        const topVouchers             = computed(() => analyticsVouchers.value?.vouchers ?? []);
        const membersAtRisk           = computed(() => analyticsRisk.value?.members ?? []);
        const riskThresholdPercentage = computed(() => analyticsRisk.value?.threshold_percentage ?? 20);
        const recentMembers           = computed(() => analyticsMemberGrowth.value?.recent_members ?? []);

        // ── Category colours bound to tenant theme ──────────────────────────────
        const categoryColors = computed(() => {
            const theme = store.state.ThemeCorporate.themeCorporate || {};
            return {
                'Branch':    theme.primary_button || '#3b82f6',
                'Merchant':  theme.sidebar_navbar || '#10b981',
                'Campaign':  theme.top_navbar     || '#f59e0b',
                'Corporate': theme.bottom_navbar   || '#1e293b',
            };
        });

        // ── Shared ApexCharts base config ───────────────────────────────────────
        const baseOptions = {
            chart: {
                fontFamily: 'Inter, Roboto, sans-serif',
                toolbar:    { show: false },
                zoom:       { enabled: false },
            },
            dataLabels: { enabled: false },
            grid: { borderColor: '#f1f5f9', strokeDashArray: 4 },
        };

        // ── Chart options (computed for reactive colours / labels) ──────────────
        const memberGrowthOptions = computed(() => ({
            ...baseOptions,
            colors: [categoryColors.value['Branch'], categoryColors.value['Corporate']],
            stroke: { curve: 'smooth', width: 3 },
            fill: {
                type: 'gradient',
                gradient: { shadeIntensity: 0.5, type: 'vertical', opacityFrom: 0.45, opacityTo: 0.02, stops: [0, 100] },
            },
            xaxis: {
                // Labels from API; empty fallback while loading
                categories:  analyticsMemberGrowth.value?.chart_labels ?? [],
                axisBorder:  { show: false },
                axisTicks:   { show: false },
                labels:      { style: { colors: '#64748b', fontSize: '11px', fontWeight: 500 } },
            },
            yaxis:  { labels: { style: { colors: '#64748b', fontSize: '11px' } } },
            legend: { show: true, position: 'top', horizontalAlign: 'right', offsetY: -20, markers: { width: 8, height: 8, radius: 12 }, itemMargin: { horizontal: 10, vertical: 0 } },
        }));

        const pointActivityOptions = computed(() => ({
            ...baseOptions,
            colors: [categoryColors.value['Branch'], categoryColors.value['Corporate']],
            stroke: { curve: 'smooth', width: 3 },
            fill: {
                type: 'gradient',
                gradient: { shadeIntensity: 0.5, type: 'vertical', opacityFrom: 0.45, opacityTo: 0.02, stops: [0, 100] },
            },
            xaxis: {
                // Labels from API; empty fallback while loading
                categories: analyticsPoints.value?.chart_labels ?? [],
                axisBorder: { show: false },
                axisTicks:  { show: false },
                labels:     { style: { colors: '#64748b', fontSize: '11px', fontWeight: 500 } },
            },
            yaxis: { labels: { style: { colors: '#64748b', fontSize: '11px' } } },
        }));

        const channelRankingOptions = computed(() => ({
            ...baseOptions,
            colors: [categoryColors.value['Branch'], categoryColors.value['Merchant'], categoryColors.value['Campaign'], categoryColors.value['Corporate']],
            labels: ['Branch', 'Merchant', 'Campaign', 'Corporate'],
            stroke: { show: false },
            plotOptions: { pie: { donut: { size: '60%' } } },
            dataLabels: {
                enabled: true,
                formatter: (val) => Math.round(val) + '%',
                style: { fontSize: '10px', fontFamily: 'Inter, sans-serif', fontWeight: '700', colors: ['#ffffff'] },
                dropShadow: { enabled: false },
            },
            legend: { show: false },
        }));

        const tierMovementOptions = computed(() => ({
            ...baseOptions,
            colors: [categoryColors.value['Branch'], categoryColors.value['Corporate']],
            plotOptions: { bar: { horizontal: false, columnWidth: '45%', borderRadius: 4 } },
            xaxis: {
                // Dynamic tier labels from API (tenant-configured tier names)
                categories: analyticsTier.value?.chart_labels ?? [],
                axisBorder: { show: false },
                axisTicks:  { show: false },
                labels:     { style: { colors: '#64748b', fontSize: '11px', fontWeight: 500 } },
            },
            yaxis: { labels: { style: { colors: '#64748b', fontSize: '11px' } } },
            tooltip: { shared: true, intersect: false },
        }));

        // ── Analytics data fetch ────────────────────────────────────────────────
        const fetchAnalytics = () => {
            // Resolve scope from selected_business_profile (same source as Home.vue load_state)
            const filterScope = {};
            const selectedProfile = store.state.Auth?.selectedBusinessProfile || JSON.parse(localStorage.getItem('selected_business_profile') || '{}');
            const branchId    = selectedProfile.branch_id    || null;
            const merchantId  = selectedProfile.merchant_id  || null;
            const corporateId = selectedProfile.corporate_id || null;

            // Priority: merchant > branch > corporate (strictest first)
            if (branchId && merchantId) {
                filterScope.merchant_id = merchantId;
            } else if (branchId) {
                filterScope.branch_id = branchId;
            } else if (merchantId) {
                filterScope.merchant_id = merchantId;
            } else if (corporateId) {
                filterScope.corporate_id = corporateId;
            }

            store.dispatch('DashboardAnalytics/fetchAll', {
                timeframe: timeframe.value,
                ...filterScope,
            }).catch((err) => {
                console.error('[Dashboard] fetchAll error:', err);
            });
        };

        // ── Scroll utilities — Channel list ─────────────────────────────────────
        const channelListRef      = ref(null);
        const showScrollIndicator = ref(false);
        const isScrolledToBottom  = ref(false);

        const checkScroll = () => {
            const el = channelListRef.value;
            if (!el) return;
            showScrollIndicator.value = el.scrollHeight > el.clientHeight;
            isScrolledToBottom.value  = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
        };
        const scrollList = () => {
            const el = channelListRef.value;
            if (!el) return;
            isScrolledToBottom.value ? el.scrollTo({ top: 0, behavior: 'smooth' }) : el.scrollBy({ top: 80, behavior: 'smooth' });
        };

        // ── Scroll utilities — Top Products ─────────────────────────────────────
        const productsListRef             = ref(null);
        const showProductsScrollIndicator = ref(false);
        const isProductsScrolledToBottom  = ref(false);

        const checkProductsScroll = () => {
            const el = productsListRef.value;
            if (!el) return;
            showProductsScrollIndicator.value = el.scrollHeight > el.clientHeight;
            isProductsScrolledToBottom.value  = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
        };
        const scrollProductsList = () => {
            const el = productsListRef.value;
            if (!el) return;
            isProductsScrolledToBottom.value ? el.scrollTo({ top: 0, behavior: 'smooth' }) : el.scrollBy({ top: 80, behavior: 'smooth' });
        };

        // ── Scroll utilities — Top Vouchers ─────────────────────────────────────
        const vouchersListRef             = ref(null);
        const showVouchersScrollIndicator = ref(false);
        const isVouchersScrolledToBottom  = ref(false);

        const checkVouchersScroll = () => {
            const el = vouchersListRef.value;
            if (!el) return;
            showVouchersScrollIndicator.value = el.scrollHeight > el.clientHeight;
            isVouchersScrolledToBottom.value  = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
        };
        const scrollVouchersList = () => {
            const el = vouchersListRef.value;
            if (!el) return;
            isVouchersScrolledToBottom.value ? el.scrollTo({ top: 0, behavior: 'smooth' }) : el.scrollBy({ top: 80, behavior: 'smooth' });
        };

        // ── Scroll utilities — Members at Risk list ─────────────────────────────
        const riskListRef             = ref(null);
        const showRiskScrollIndicator = ref(false);
        const isRiskScrolledToBottom  = ref(false);

        const checkRiskScroll = () => {
            const el = riskListRef.value;
            if (!el) return;
            showRiskScrollIndicator.value = el.scrollHeight > el.clientHeight;
            isRiskScrolledToBottom.value  = el.scrollTop + el.clientHeight >= el.scrollHeight - 5;
        };
        const scrollRiskList = () => {
            const el = riskListRef.value;
            if (!el) return;
            isRiskScrolledToBottom.value ? el.scrollTo({ top: 0, behavior: 'smooth' }) : el.scrollBy({ top: 80, behavior: 'smooth' });
        };

        // ── Percentage helpers for progress bars ────────────────────────────────
        const totalRegistrations = computed(() => registrationSources.value.reduce((acc, s) => acc + s.count, 0));
        const getSourcePercentage  = (countVal) => totalRegistrations.value === 0 ? 0 : Math.round((countVal / totalRegistrations.value) * 100);
        const getProductPercentage = (qty)  => topProducts.value.length === 0 ? 0 : Math.round((qty / topProducts.value[0].qty) * 100);
        const getVoucherPercentage = (countVal) => topVouchers.value.length === 0 ? 0 : Math.round((countVal / topVouchers.value[0].claim_count) * 100);

        // ── Mount & watch ───────────────────────────────────────────────────────
        onMounted(() => {
            fetchAnalytics();
            setTimeout(checkScroll,         200);
            setTimeout(checkProductsScroll, 200);
            setTimeout(checkVouchersScroll, 200);
            setTimeout(checkRiskScroll,     200);
        });

        watch(() => props.externalTimeframe, (newVal) => {
            if (newVal) {
                timeframe.value = newVal;
                fetchAnalytics();
                setTimeout(checkScroll,         200);
                setTimeout(checkProductsScroll, 200);
                setTimeout(checkVouchersScroll, 200);
                setTimeout(checkRiskScroll,     200);
            }
        });

        // Auto reload when business profile is switched via navbar
        watch(() => store.state.Auth.selectedBusinessProfile, () => {
            fetchAnalytics();
        }, { deep: true });

        watch(registrationSources, () => setTimeout(checkScroll,         200));
        watch(topProducts,         () => setTimeout(checkProductsScroll, 200));
        watch(topVouchers,         () => setTimeout(checkVouchersScroll, 200));
        watch(membersAtRisk,       () => setTimeout(checkRiskScroll,     200));
        watch(() => props.dashboardMode, () => {
            fetchAnalytics();
            setTimeout(checkScroll,         200);
            setTimeout(checkProductsScroll, 200);
            setTimeout(checkVouchersScroll, 200);
            setTimeout(checkRiskScroll,     200);
        });

        return {
            isCorporateLevel,
            timeframe,
            timeframeText,
            activePointsSlide,
            analyticsKpi,
            analyticsLoading,
            analyticsError,
            retryFetch,
            kpiTotalMembers,
            kpiTransactions,
            kpiVouchers,
            kpiPointsEarned,
            kpiPointsSpent,
            memberGrowthSeries,
            memberGrowthOptions,
            channelRankingSeries,
            channelRankingOptions,
            pointActivitySeries,
            pointActivityOptions,
            tierMovementSeries,
            tierMovementOptions,
            registrationSources,
            categoryColors,
            topProducts,
            topVouchers,
            membersAtRisk,
            riskThresholdPercentage,
            recentMembers,
            fetchAnalytics,
            formatCurrency,
            formatNumber,
            getProductPercentage,
            getVoucherPercentage,
            getSourcePercentage,
            channelListRef,
            showScrollIndicator,
            isScrolledToBottom,
            checkScroll,
            scrollList,
            productsListRef,
            showProductsScrollIndicator,
            isProductsScrolledToBottom,
            checkProductsScroll,
            scrollProductsList,
            vouchersListRef,
            showVouchersScrollIndicator,
            isVouchersScrolledToBottom,
            checkVouchersScroll,
            scrollVouchersList,
            riskListRef,
            showRiskScrollIndicator,
            isRiskScrolledToBottom,
            checkRiskScroll,
            scrollRiskList,
            showSwitchModal,
            switchTargetRoute,
            switchRequiredLevel,
            performQuickAction,
            pausePointsSlide,
            resumePointsSlide,
            setPointsSlide,
        };
    },
}
</script>

<style scoped>
.dashboard-analytics-container {
    padding: 4px 24px 24px 0;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: #1e293b;
    min-height: 100vh;
}

/* Typography */
.dashboard-title    { color: #0f172a; letter-spacing: -0.025em; font-size: 1.75rem; }
.dashboard-subtitle { color: #64748b; font-size: 0.925rem; max-width: 600px; }

.role-badge {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 0.725rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.filter-selector {
    border-radius: 8px;
    border: 1px solid #cbd5e1;
    color: #334155;
    font-size: 0.85rem;
    padding: 8px 36px 8px 12px;
    background-color: #ffffff;
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
    font-weight: 600;
}
.filter-selector:focus { border-color: var(--primary-color); box-shadow: 0 0 0 2px rgba(0,0,0,0.05); outline: none; }

/* KPI Cards */
.analytic-kpi-card {
    background: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 24px;
    position: relative;
    min-height: 160px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    transition: transform 0.22s ease-in-out, border-color 0.22s ease-in-out, box-shadow 0.22s ease-in-out;
    box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}
.analytic-kpi-card:hover {
    transform: translateY(-2px);
    border-color: var(--primary-color, #F27178);
    box-shadow: 0 6px 16px rgba(242,113,120,0.08);
}

.quick-action-card { cursor: pointer; }
.kpi-icon-wrapper .hover-icon { display: none; }
.quick-action-card:hover .kpi-icon-wrapper .default-icon { display: none; }
.quick-action-card:hover .kpi-icon-wrapper .hover-icon   { display: inline-block; }

.action-btn {
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
}
.analytic-chart-card:hover .action-btn,
.analytic-table-card:hover .action-btn { opacity: 1; }

.kpi-label { font-size: 0.75rem; font-weight: 700; letter-spacing: 0.05em; color: #64748b; }
.kpi-value { color: #0f172a; font-size: 2rem; letter-spacing: -0.03em; line-height: 1; }

.trend-indicator {
    font-size: 0.725rem;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.trend-up   { background-color: #dcfce7; color: #15803d; }
.trend-down { background-color: #fee2e2; color: #b91c1c; }
.kpi-compare-text { font-size: 0.725rem; color: #64748b; font-weight: 500; }

.icon-circle { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 1.1rem; }
.primary-icon-circle {
    background-color: color-mix(in srgb, var(--primary-color, #F27178) 12%, transparent);
    color: var(--primary-color, #F27178);
}

/* Cycling Points Slide */
.cycling-dots { display: flex; gap: 4px; position: absolute; bottom: 12px; right: 24px; }
.cycle-dot { width: 5px; height: 5px; border-radius: 50%; background-color: #cbd5e1; transition: background-color 0.3s; }
.cycle-dot.active { background-color: var(--primary-color); }

.points-slide-enter-active, .points-slide-leave-active { transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
.points-slide-enter-from  { opacity: 0; transform: translateY(10px); }
.points-slide-leave-to    { opacity: 0; transform: translateY(-10px); }

/* Chart & Table Cards */
.analytic-chart-card,
.analytic-table-card {
    background: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    padding: 28px;
    box-shadow: 0 1px 2px rgba(15,23,42,0.04);
}

/* Channel Distribution */
.color-dot { width: 10px; height: 10px; border-radius: 3px; display: inline-block; }
.channel-list-detailed {
    max-height: 190px;
    overflow-y: auto;
    padding-left: 28px;
    padding-right: 28px;
    margin-left: -28px;
    margin-right: -28px;
    scrollbar-width: none;
}
.channel-list-detailed::-webkit-scrollbar { display: none; }
.scroll-more-btn {
    position: absolute;
    bottom: -8px;
    left: 50%;
    transform: translateX(-50%);
    background-color: transparent;
    border: none;
    width: auto;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: none;
    color: #cbd5e1;
    font-size: 0.9rem;
    z-index: 10;
    transition: color 0.2s ease-in-out, transform 0.2s ease-in-out;
}
.scroll-more-btn:hover { color: var(--primary-color, #F27178); transform: translateX(-50%) scale(1.2); }
.channel-detail-row { border-bottom: 1px dashed #f1f5f9; }
.channel-detail-row:last-child { border-bottom: none; }

/* Recent Registrations Ticker */
.recent-registrations-ticker { width: 100%; overflow: hidden; border-top: 1px dashed #e2e8f0; padding-top: 16px; }
.ticker-viewport {
    width: 100%;
    overflow: hidden;
    position: relative;
    padding: 6px 0;
    mask-image: linear-gradient(to right, transparent, #000 10%, #000 90%, transparent);
    -webkit-mask-image: linear-gradient(to right, transparent, #000 10%, #000 90%, transparent);
}
.ticker-track { display: flex; width: max-content; animation: scroll-ticker 50s linear infinite; }
.ticker-viewport:hover .ticker-track { animation-play-state: paused; }
.ticker-group { display: flex; gap: 16px; padding-right: 16px; }
.ticker-item-card {
    background-color: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
    padding: 10px 14px;
    min-width: 210px;
    box-shadow: 0 1px 3px rgba(15,23,42,0.02);
    transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
}
.ticker-item-card:hover { transform: translateY(-2px); border-color: var(--primary-color, #F27178); box-shadow: 0 4px 10px rgba(0,0,0,0.04); }
.member-avatar-wrapper { flex-shrink: 0; }
.member-avatar { width: 32px; height: 32px; border-radius: 50%; object-fit: cover; border: 1.5px solid #ffffff; box-shadow: 0 0 0 1px #e2e8f0; }
.member-avatar-fallback { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.725rem; font-weight: 700; }
.member-info { min-width: 0; text-align: left !important; }
.member-name { font-size: 0.775rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.member-meta { display: flex; align-items: center; gap: 4px; font-size: 0.65rem; color: #64748b; }
.meta-separator { color: #cbd5e1; }
.reg-source { font-weight: 600; }

@keyframes scroll-ticker {
    0%   { transform: translateX(0); }
    100% { transform: translateX(-50%); }
}

/* Premium Tables */
.table-responsive { background: transparent !important; }
.premium-table { background: transparent !important; --bs-table-bg: transparent !important; border-collapse: collapse; }
.premium-table th, .premium-table tr, .premium-table td { background: transparent !important; --bs-table-bg: transparent !important; }
.premium-table th { font-size: 0.725rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; color: #64748b; padding: 12px 8px 12px 0; border-bottom: 1px solid #cbd5e1; }
.premium-table td { padding: 16px 8px 16px 0; border-bottom: 1px solid #f1f5f9; }
.premium-table tbody tr { transition: background-color 0.15s ease-in-out; }
.premium-table tbody tr:hover { background-color: #f8fafc !important; }
.premium-table tr:last-child td { border-bottom: none; }

.rank-badge { width: 24px; height: 24px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; }
.rank-1 { background-color: #fef3c7; color: #d97706; }
.rank-2 { background-color: #f1f5f9; color: #475569; }
.rank-3 { background-color: #ffedd5; color: #c2410c; }
.rank-4, .rank-5 { background-color: #f8fafc; color: #94a3b8; }

.progress-bar-container { width: 100%; max-width: 120px; height: 6px; background-color: #e2e8f0; border-radius: 3px; overflow: hidden; }
.progress-bar-fill { height: 100%; border-radius: 3px; }

/* Members at Risk */
.avatar-circle { width: 32px; height: 32px; background-color: #f1f5f9; color: #475569; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; }
.tier-pill { background-color: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; padding: 1px 6px; border-radius: 4px; font-size: 0.65rem; font-weight: 600; }
.alert-pill { background-color: #fee2e2; color: #b91c1c; padding: 4px 8px; border-radius: 6px; font-size: 0.7rem; font-weight: 700; display: inline-flex; align-items: center; }

/* Utilities */
.text-dark-slate  { color: #0f172a !important; }
.text-slate-muted { color: #64748b !important; }
.small-text       { font-size: 0.7rem; }

/* Custom Tooltips */
.custom-entity-tip {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    background: #1e293b;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 11px;
    white-space: nowrap;
    z-index: 100;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    pointer-events: none;
    margin-bottom: 8px;
}
.custom-entity-tip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border-width: 5px;
    border-style: solid;
    border-color: #1e293b transparent transparent transparent;
}
.fade-enter-active, .fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from, .fade-leave-to {
    opacity: 0;
}
.tooltip-trigger-icon {
    cursor: help;
    font-size: 13px;
    color: var(--primary-color);
}
.tooltip-text-trigger {
    cursor: help;
}
.tooltip-text-trigger:hover {
    color: var(--primary-color);
}
.tooltip-row-icon {
    width: 14px;
    text-align: center;
}
</style>